import { type ReactElement, Suspense } from 'react';
import { Navigate } from 'react-router';

import type { RouteResolver } from '~/routing/types';

import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { FlagType } from '~/flags/flags';
import { useFlags } from '~/flags/useFlags';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { usePermissions } from '~/hooks/usePermissions';
import NotFoundPage from '~/pages/404';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';
import { RbacPermission } from '~/types/Rbac';

import { resolveComponent } from './resolveComponent';

interface Props {
  path: RoutePathType;
  resolvers: RouteResolver[];
  permissions?: RbacPermission[];
  flag?: FlagType;
}

export function RouteGate({ path, resolvers, permissions, flag }: Props) {
  const flags = useFlags();
  const { params } = useRouting();
  const currentUser = useCurrentUser();

  if (flag && !flags[flag]) {
    return <NotFoundPage />;
  }

  // Resolvers that branch on `permission(user)` need a hydrated user, otherwise
  // they evaluate against an empty user object and fall through to a redirect
  // before the real user loads. Hold rendering until the user is populated.
  const isUserHydrated = Object.keys(currentUser).length > 0;
  const hasPermissionResolvers = resolvers.some(
    (resolver) => resolver.permission,
  );
  if (hasPermissionResolvers && !isUserHydrated) {
    return <ContentContainer loadingContent={{ isLoading: true }} />;
  }

  const context = {
    routeParams: params,
    user: currentUser,
  };

  const Resolved = resolveComponent(resolvers, context);

  // Key by path so React unmounts/remounts the gate (and its
  // `usePermissions` state) when the matched route changes. Without this
  // the same `RouteGate` instance is reconciled across route transitions
  // and leaks stale permission results into the new route.
  return (
    <PermissionsGate key={path} permissions={permissions}>
      <Suspense fallback={null}>
        <Resolved />
      </Suspense>
    </PermissionsGate>
  );
}

interface PermissionsGateProps {
  permissions?: RbacPermission[];
  children: ReactElement;
}

/**
 * Per-route RBAC gate. When `permissions` are provided, renders children
 * immediately and redirects to /unauthorized once permissions finish loading
 * without access. Routes without `permissions` pass through unchanged.
 */
function PermissionsGate({ permissions, children }: PermissionsGateProps) {
  const { isLoading, hasSomePermissions } = usePermissions({ permissions });

  if (!permissions?.length) {
    return children;
  }

  if (!isLoading && !hasSomePermissions) {
    return <Navigate to={RoutePathType.Unauthorized} replace />;
  }

  return children;
}
